import React, { useRef } from 'react';
import { Box, Button, Card, CardActionArea, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import { basicMessageDialog, MessageDialog } from "../../../../components/message-dialog";

interface LogoDialogProps {
    open: boolean;
    onClose: () => void;
    onFileSelect: (file: File) => void;
    onLogoChange: (base64: string) => void;
    logoPreview: string|null;
}

const LogoDialog: React.FC<LogoDialogProps> = ({ open, onClose, onFileSelect, onLogoChange, logoPreview }) => {
    const [state, setState] = React.useState({
        dialog: basicMessageDialog()
    });
    const [localLogo, setLocalLogo] = React.useState<string | null>(logoPreview);
    const imageInputRef = useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (open) setLocalLogo(logoPreview);
    }, [open, logoPreview]);

    const MAX_SIZE_MB = 5 * 1024 * 1024;

    const openDialog = (message: string) => {
        setState(prev => ({
            ...prev,
            dialog: basicMessageDialog(message, closeDialog, true)
        }));
    };

    const closeDialog = () => {
        setState(prev => ({ ...prev, dialog: basicMessageDialog() }));
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > MAX_SIZE_MB) {
                openDialog("O arquivo deve ter no máximo 5MB.");
                event.target.value = '';
                return;
            }
            if (!file.type.startsWith('image/')) {
                openDialog("Apenas imagens são permitidas. Envie um arquivo PNG, JPG ou similar.");
                event.target.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result as string;
                onFileSelect(file)
                setLocalLogo(base64);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = () => {
        if (localLogo) {
            onLogoChange(localLogo);
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <MessageDialog {...state.dialog}/>
            <DialogTitle></DialogTitle>
            <DialogContent>
                <Box display="flex" gap={2} justifyContent="center" mb={2}>
                    {/* Card para imagem */}
                    <Card variant="outlined" sx={{ border: '2px solid #C0985D', textAlign: 'center' }}>
                        <CardActionArea onClick={() => imageInputRef.current?.click()} sx={{ p: 2 }}>
                            <InsertPhotoIcon sx={{ fontSize: 40, color: '#C0985D' }}/>
                            <Box mt={1}>Selecionar nova imagem</Box>
                        </CardActionArea>
                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                            data-testid="logo-upload-image"
                        />
                    </Card>
                </Box>
                {localLogo && (
                    <img src={localLogo} alt="Logo Preview"
                         style={{ maxWidth: 500, maxHeight: 300, display: 'block', margin: '0 auto' }}/>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant={'outlined'} onClick={onClose} color="primary">Fechar</Button>
                <Button variant={"contained"} onClick={handleSave}>Salvar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LogoDialog;
